import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { ImageProps } from "@outschool/backpack";
import { ContentfulBanner, User } from "@outschool/gql-backend-generated";
import {
  CampaignFragment,
  LoadContentfulBannersQuery,
  LoadContentfulBannersQueryVariables,
} from "@outschool/gql-frontend-generated";
import { useLocale } from "@outschool/localization";
import { dayjs } from "@outschool/time";
import { gql, useQueryWithPreviousData } from "@outschool/ui-apollo";
import {
  ExternalLink,
  TrackedButton,
  useFeatureFlag,
} from "@outschool/ui-components-shared";
import { useServerExperimentContext } from "@outschool/ui-experiments";
import * as _ from "lodash";
import React from "react";

import MarketingBanner from "../../Banners/MarketingBanner";
import { ParentCtaInterface } from "./types";

/**
 * note: rendering this component in ssr throws an error that location can't be found on undefined.
 *       feel strongly that it's the iplookup-client. must fix before transitioning banner to ssr
 */
import type { IPContext } from "@outschool/iplookup-client";

const LoadContentfulBanners = gql`
  query LoadContentfulBanners(
    $preview: Boolean!
    $locale: String
    $placement: String!
  ) {
    contentfulBannerPlacementCollection(
      preview: $preview
      locale: $locale
      limit: 1
      where: { name: $placement }
    ) {
      total
      items {
        name
        bannersCollection {
          total
          items {
            name
            campaign {
              ...Campaign
            }
            title
            content {
              json
            }
            buttonText
            url
            image {
              title
              description
              url
            }
          }
        }
      }
    }
  }

  fragment Campaign on ContentfulCampaign {
    name
    startAt
    endAt
    countryCollection(limit: 10) {
      items {
        name
        code
        matchingText
      }
    }
    stateOrProvinceCollection(limit: 50) {
      items {
        name
        code
      }
    }
    schoolingApproaches
    minAge
    maxAge
    onlyForTreatmentInExperiment
    hasSubscription
  }
`;

/**
 * @param placement name of BannerPlacement
 * @param previewUnpublished causes Contentful query to include unpublished content
 */
export function useContentfulBanners(
  placement: string,
  previewUnpublished: boolean = false,
  isFullWidth: boolean = false
): { loading: boolean; banners?: ParentCtaInterface[]; isFullWidth?: boolean } {
  const locale = useLocale();
  const useContentfulBanners = useFeatureFlag(
    "discovery-contentful-home-page-banners"
  );

  const { experimentAssignments, experimentOverrides } =
    useServerExperimentContext();

  const { data, loading } = useQueryWithPreviousData<
    LoadContentfulBannersQuery,
    LoadContentfulBannersQueryVariables
  >(LoadContentfulBanners, {
    variables: { placement, preview: previewUnpublished, locale },
    skip: !useContentfulBanners,
  });

  const banners: ParentCtaInterface[] | undefined = React.useMemo(() => {
    if (loading) {
      return undefined;
    }

    const userExperiments = {
      ...experimentAssignments,
      ...experimentOverrides,
    };
    return data?.contentfulBannerPlacementCollection?.items?.[0]?.bannersCollection?.items.map(
      (item: ContentfulBanner) =>
        makeCtaFromContentfulBanner(
          item,
          placement,
          isFullWidth,
          userExperiments
        )
    );
  }, [
    loading,
    data,
    placement,
    isFullWidth,
    experimentAssignments,
    experimentOverrides,
  ]);

  return {
    loading,
    banners,
  };
}

export function makeCtaFromContentfulBanner(
  // @ts-ignore TS(2339): Property 'items' does not exist on type '{ __typen... Remove this comment to see the full error message
  banner: LoadContentfulBannersQuery["contentfulBannerPlacementCollection"]["items"][0]["bannersCollection"]["items"][0],
  placement: string,
  isFullWidth: boolean,
  userExperiments?: Record<string, string>
): ParentCtaInterface {
  return {
    name: banner.name,
    //@ts-ignore
    isEligible({
      ipInfo,
      currentUser,
    }: {
      ipInfo: IPContext["ipInfo"];
      currentUser: User;
    }) {
      return (
        checkCampaignDate(banner.campaign) &&
        checkCampaignState(banner.campaign, ipInfo, currentUser) &&
        checkCampaignCountry(banner.campaign, ipInfo, currentUser) &&
        checkUserLearnersSchoolingApproach(banner.campaign, currentUser) &&
        checkUserLearnersWithinAgeRange(banner.campaign, currentUser) &&
        checkExperiments(banner.campaign, userExperiments) &&
        checkUserSubscriptionMatch(banner.campaign, currentUser)
      );
    },
    Component: ({
      renderImage,
    }: { renderImage?: (props: ImageProps) => React.ReactElement } = {}) => (
      <MarketingBanner
        renderImage={renderImage}
        imagePath={banner.image?.url}
        // TODO: figure out how to translate text inside json
        content={documentToReactComponents(banner.content.json)}
        title={banner.title}
        isFullWidth={isFullWidth}
        cta={
          <TrackedButton
            variant="contained"
            component={ExternalLink}
            url={banner.url}
            trackingName="marketing_banner_cta"
            trackingUniqueId={banner.name}
            trackingSharedProperties={{
              placement,
            }}
            trackViews={true}
          >
            {banner.buttonText}
          </TrackedButton>
        }
      />
    ),
  };
}

function checkCampaignDate(campaign: CampaignFragment): boolean {
  if (campaign.startAt && dayjs().isBefore(campaign.startAt)) {
    return false;
  }
  if (campaign.endAt && dayjs().isAfter(campaign.endAt)) {
    return false;
  }
  return true;
}

function checkCampaignState(
  campaign: CampaignFragment,
  ipInfo: IPContext["ipInfo"],
  currentUser: User
): boolean {
  if (campaign?.stateOrProvinceCollection?.items?.length === 0) {
    return true;
  }
  const userState = currentUser?.location?.state;
  return (
    campaign?.stateOrProvinceCollection?.items?.some(state => {
      if (userState) {
        return state?.name === userState;
      } else {
        return ipInfo?.subdivisions?.find(
          subdivision => subdivision?.isoCode === state?.code
        );
      }
    }) ?? false
  );
}

function checkCampaignCountry(
  campaign: CampaignFragment,
  ipInfo: IPContext["ipInfo"],
  currentUser: User
): boolean {
  if (campaign?.countryCollection?.items?.length === 0) {
    return true;
  }
  const userCountry =
    currentUser?.location?.address?.split(",")?.pop()?.trim() ?? null;
  return (
    campaign?.countryCollection?.items?.some(country => {
      if (userCountry) {
        return country?.matchingText?.includes(userCountry);
      } else {
        return ipInfo?.country?.isoCode === country?.code;
      }
    }) ?? false
  );
}

// Creating a local map to match display strings from db with values in contentful.
// SHIM: Ideally we can remove this once data is normalized in the database.
const LEARNER_SCHOOL_APPROACH_MAP: Record<string, string | undefined> = {
  Other: "other",
  Homeschool: "homeschool",
  "Public/Private School": "public_private_school",
};

function checkUserLearnersSchoolingApproach(
  campaign: CampaignFragment,
  currentUser: User
): boolean {
  if (
    !campaign?.schoolingApproaches ||
    campaign?.schoolingApproaches?.length === 0
  ) {
    return true;
  }

  // If we do have schoolingApproaches defined but no user, don't show the banner.
  if (!currentUser) {
    return false;
  }

  const childrenSchoolApproaches = currentUser.children
    .flatMap(
      child =>
        child.attributes.find(attr => attr.key === "schooling_approach") ?? []
    )
    .map(attr => LEARNER_SCHOOL_APPROACH_MAP[attr.value]);

  // Map values need to be updated if we see undefined values, logging error to prevent silent failures.
  if (childrenSchoolApproaches.some(approach => approach === undefined)) {
    OsPlatform.captureError(
      new Error(
        "Contentful banner missing string mapping for LEARNER_SCHOOL_APPROACH_MAP, did we add more school_approaches to entity_attributes?"
      ),
      {
        tags: {
          package: "ui-components-website",
        },
      }
    );
  }

  return (
    _.intersection(childrenSchoolApproaches, campaign?.schoolingApproaches)
      .length > 0
  );
}

function checkUserLearnersWithinAgeRange(
  campaign: CampaignFragment,
  currentUser: User
): boolean {
  if (!campaign?.minAge && !campaign?.maxAge) {
    return true;
  }

  // If we do have age settings defined but no user, don't show the banner.
  if (!currentUser) {
    return false;
  }

  const childrenAges = currentUser.children.flatMap(child => child.age ?? []);

  return (
    childrenAges.some(age =>
      campaign?.minAge ? age >= campaign?.minAge : true
    ) &&
    childrenAges.some(age =>
      campaign?.maxAge ? age <= campaign?.maxAge : true
    )
  );
}

function checkExperiments(
  campaign: CampaignFragment,
  userExperiments?: Record<string, string>
): boolean {
  if (!userExperiments || !campaign?.onlyForTreatmentInExperiment) {
    return true;
  }
  return userExperiments[campaign.onlyForTreatmentInExperiment] === "treatment";
}

// Check if the user has a subscription that matches the campaign's subscription requirement
function checkUserSubscriptionMatch(
  campaign: CampaignFragment,
  currentUser: User
): boolean {
  if (
    campaign?.hasSubscription === undefined ||
    campaign?.hasSubscription === null
  ) {
    return true;
  }

  return currentUser?.hasSubscription === campaign.hasSubscription;
}
